/* eslint-disable no-param-reassign */
import { FormHandles } from '@unform/core';
import axios from 'axios';
import produce from 'immer';
import Notiflix from 'notiflix';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import Modal2 from '../..';
import QuestionnaireService from '../../../../services/modules/questionnaire/questionnaire.service';
import {
  GetQuestionnaire,
  SendQuestionnaire,
} from '../../../../services/modules/questionnaire/types';
import getValidationErrors from '../../../../utils/getValidationErrors';
import Button from '../../../Button';
import FormWrapper from '../../../FormWrapper';
import { Helper } from './helper';

import { Container, FormContent } from './styles';
import UI from './uiConstructor';

type QuestionaireData = GetQuestionnaire['return'];
type ToSend = SendQuestionnaire['toApi'];

const validation = Yup.array().test(
  'Tudo ok',
  'Campo obrigatório.',
  (value) => {
    if (!value) return false;
    return value.length > 0 && value.filter(Boolean).length > 0;
  },
);
const rnValidation = ({ min, max }: { min: number; max: number }) => {
  return Yup.array().test(
    'Tudo ok',
    `Valor inválido. Limites de ${min} até ${max}.`,
    (value) => {
      if (!value) return false;
      return (
        value.length > 0 &&
        value.filter(Boolean).length > 0 &&
        value.every((v) => {
          const vParsed = Number(v);
          return vParsed >= min && vParsed <= max;
        })
      );
    },
  );
};
type IShapeConstructor = { [k: string]: typeof validation };

const QuestionnaireForm: React.FC<{
  data: QuestionaireData;
  enterpriseWebUserName?: string;
}> = ({ data: { questions: _data, title }, enterpriseWebUserName }) => {
  const formRef = useRef<FormHandles>(null);
  const data = useMemo(() => Helper.removeDuplicateQuestion(_data), [_data]);
  const allDependentsData = useMemo(() => {
    return data.filter((question) => {
      return (
        'conditions' in question &&
        Array.isArray(question.conditions) &&
        question.conditions.length > 0
      );
    });
  }, [data]);

  const shapeConstructor = useRef<IShapeConstructor>({});

  const [allowedConditions, setAllowedConditions] = useState<
    Record<number, Record<number, boolean>>
  >({});

  const filteredInputs = useMemo(() => {
    const allowed = data.filter((question) => {
      const nestedQuestion = allDependentsData.find(
        (nestQuestion) => nestQuestion.idQuestion === question.idQuestion,
      );

      // question not dependent on any other question
      if (!nestedQuestion) return true;

      const conditions = allowedConditions[nestedQuestion.idQuestion];
      if (conditions) {
        const nestedConditions = Object.entries(conditions);
        return nestedConditions.every(([, value]) => !!value);
      }
      return false;
    });
    return allowed;
  }, [data, allDependentsData, allowedConditions]);

  const handleSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      try {
        Notiflix.Block.circle('.notiflix-questionnaire-submit-button');

        const shapeData = Object.entries(formData).reduce(
          (acc, [questionId, value]) => {
            acc[questionId] = Helper.normalizeValues(questionId, value);
            return acc;
          },
          {} as { [key: string]: string[] },
        );
        const shape = Yup.object().shape(
          Object.entries(shapeConstructor.current).reduce(
            (acc, [questionId, validationSchema]) => {
              acc[questionId] = validationSchema;
              if (questionId.includes('rn|')) {
                const key = questionId.replace('rn|', '');
                const questionData = data.find(
                  (question) => String(question.idQuestion) === key,
                );
                acc[questionId] = rnValidation({
                  max: questionData?.maxValue ?? Infinity,
                  min: questionData?.minValue ?? -Infinity,
                });
              }
              return acc;
            },
            {} as IShapeConstructor,
          ),
        );
        await shape.validate(shapeData, { abortEarly: false });

        const prepareData: ToSend['answers'] = Object.entries(shapeData).map(
          ([questionId, value]) => {
            const [, idQuestion] = questionId.split('|');
            return {
              idQuestion: Number(idQuestion),
              answer: value,
            };
          },
        );

        const preparedDataNotInformed = data
          .filter(
            (question) =>
              !filteredInputs.some(
                (q) => String(q.idQuestion) === String(question.idQuestion),
              ),
          )
          .map((question) => ({
            idQuestion: Number(question.idQuestion),
            answer: [],
          }));

        // console.log('Form submitted:', formData);
        // console.log('Form parsed:', prepareData);
        // console.log('Not informed inputs:', preparedDataNotInformed);

        await QuestionnaireService.sendQuestionnaire({
          answers: {
            ...prepareData,
            ...preparedDataNotInformed,
          },
          enterpriseWebUsername: enterpriseWebUserName ?? '',
        });
        Notiflix.Block.remove('.notiflix-questionnaire-submit-button');
        Notiflix.Notify.success('Pesquisa enviada com sucesso!');
        Modal2.Close();
      } catch (err) {
        Notiflix.Block.remove('.notiflix-questionnaire-submit-button');
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          const showErrorOnLastCheckboxOptionIfNecessary = () => {
            Object.entries(errors).forEach(([key, errorMessage]) => {
              const [type, idQuestion] = key.split('|');
              if (type === 'em') {
                const question = filteredInputs.find(
                  (q) => String(q.idQuestion) === String(idQuestion),
                );
                if (question) {
                  const lastOption =
                    question.options?.[question.options.length - 1].idOption;
                  formRef.current?.setFieldError(
                    `em|${idQuestion}[${lastOption}]`,
                    errorMessage,
                  );
                }
              }
            });
          };
          showErrorOnLastCheckboxOptionIfNecessary();
        } else if (axios.isAxiosError(err)) {
          console.error('Error:', err);
          Notiflix.Notify.failure(
            'Ops... Não foi possível enviar a pesquisa. Tente novamente mais tarde.',
          );
        } else {
          console.error('Unexpected error:', err);
          Notiflix.Notify.failure(
            'Ops... Ocorreu um erro inesperado. Tente novamente mais tarde.',
          );
        }
      }
    },
    [filteredInputs, data, enterpriseWebUserName],
  );

  const onChange = useCallback(
    <T,>(inputName: string, questionId: number) => {
      return (newValue?: unknown) => {
        const dirty = (newValue ?? formRef.current?.getData()[inputName]) as T;
        const normalizedValue = Helper.normalizeValues(inputName, dirty);

        allDependentsData.forEach((question) => {
          const nestedCondition = question.conditions?.find(
            (cond) => cond.idQuestion === questionId,
          );
          if (nestedCondition) {
            const nestedOptionValue = String(nestedCondition.idOption);
            const satisfies = normalizedValue.includes(nestedOptionValue);
            setAllowedConditions((prevConditions) =>
              produce(prevConditions, (draft) => {
                if (!draft[question.idQuestion]) {
                  draft[question.idQuestion] = {};
                }
                draft[question.idQuestion][nestedCondition.idQuestion] =
                  satisfies;

                if (!satisfies) {
                  Object.entries(draft).forEach(([k, deps]) => {
                    if (deps[question.idQuestion]) {
                      draft[Number(k)][question.idQuestion] = false;
                    }
                  });
                }
              }),
            );
          }
        });

        if (inputName.startsWith('em|')) {
          const [type, idQuestion] = inputName.split('|');
          if (type === 'em') {
            const question = filteredInputs.find(
              (q) => String(q.idQuestion) === String(idQuestion),
            );
            if (question) {
              const lastOption =
                question.options?.[question.options.length - 1].idOption;
              formRef.current?.setFieldError(
                `em|${idQuestion}[${lastOption}]`,
                '',
              );
            }
          }
        }
      };
    },
    [allDependentsData, filteredInputs],
  );

  useEffect(() => {
    shapeConstructor.current = filteredInputs.reduce((acc, question) => {
      const key = `${question.questionType.toLowerCase()}|${
        question.idQuestion
      }`;
      acc[key] = validation;
      return acc;
    }, {} as { [k: string]: typeof validation });
  }, [filteredInputs]);

  return (
    <Container>
      <h1 dangerouslySetInnerHTML={{ __html: title }} />
      <FormWrapper ref={formRef} onSubmit={handleSubmit} formLook>
        <FormContent>
          {filteredInputs.map((question) => {
            switch (question.questionType) {
              case 'EU':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.RadioGroup({
                      idQuestion: question.idQuestion,
                      label: question.question,
                      name: `eu|${question.idQuestion}`,
                      options: question.options ?? [],
                      tooltip: question.tooltip ?? '',
                      onChangeFireCallback: onChange(
                        `eu|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'EM':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.CheckboxGroup({
                      idQuestion: question.idQuestion,
                      label: question.question,
                      name: `em|${question.idQuestion}`,
                      options: question.options ?? [],
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `em|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `em|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'RN':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.NumberInput({
                      idQuestion: question.idQuestion,
                      name: `rn|${question.idQuestion}`,
                      label: question.question,
                      max: question.maxValue ?? Infinity,
                      min: question.minValue ?? -Infinity,
                      precision: question.precision ?? 0,
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `rn|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `rn|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'RD':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.Datepicker({
                      idQuestion: question.idQuestion,
                      name: `rd|${question.idQuestion}`,
                      label: question.question,
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `rd|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `rd|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'CT':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.TextArea({
                      idQuestion: question.idQuestion,
                      name: `ct|${question.idQuestion}`,
                      label: question.question,
                      characterLimit: question.characterLimit ?? 2000,
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `ct|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `ct|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'TA':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.Text({
                      idQuestion: question.idQuestion,
                      name: `ta|${question.idQuestion}`,
                      label: question.question,
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `ta|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `ta|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'LS':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.List({
                      idQuestion: question.idQuestion,
                      name: `ls|${question.idQuestion}`,
                      label: question.question,
                      options: question.options ?? [],
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `ls|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `ls|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );
              case 'TG':
                return (
                  <React.Fragment key={`question-${question.idQuestion}`}>
                    {UI.Tags({
                      idQuestion: question.idQuestion,
                      name: `tg|${question.idQuestion}`,
                      label: question.question,
                      tooltip: question.tooltip ?? '',
                      dispatchManualError: (errorMessage: string) => {
                        formRef.current?.setFieldError(
                          `tg|${question.idQuestion}`,
                          errorMessage,
                        );
                      },
                      onChangeFireCallback: onChange(
                        `tg|${question.idQuestion}`,
                        question.idQuestion,
                      ),
                    })}
                  </React.Fragment>
                );

              default:
                return null;
            }
          })}
        </FormContent>

        <Button
          type="submit"
          greenButton
          autoWidth
          formLook
          className="marginLeft notiflix-questionnaire-submit-button"
          style={{ alignSelf: 'end' }}
        >
          Enviar
        </Button>
      </FormWrapper>
    </Container>
  );
};

export default QuestionnaireForm;
